/*
Risk Scoring Journey service.

Wraps /api/v1/rsk/risks/{id}/scores and /api/v1/rsk/scores/{id}. Each row is
one snapshot of a risk's L/S, control existence + effectiveness, computed
inherent/residual, the date of test, who scored it, and (optionally) the
assessment program under which the score was captured. Multiple snapshots
form the risk's journey.
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

export interface RiskScore {
  id: number;
  entity_id: number;
  risk_id: number;
  assessment_instance_id?: number | null;
  program_code?: string | null;
  likelihood: number;
  severity: number;
  inherent_risk: number;
  control_existence: number;
  control_effectiveness: number;
  control_risk: number;
  residual_risk: number;
  date_of_test?: string | null;
  notes?: string | null;
  is_active: number;
  created_by: number;
  created_by_name?: string | null;
  created_on: string;
  modified_by?: number | null;
  modified_on?: string | null;
}

export interface RiskScoreInput {
  likelihood: number;
  severity: number;
  control_existence: number;
  control_effectiveness: number;
  date_of_test?: string;
  assessment_instance_id?: number | null;
  notes?: string;
}

export interface AssessmentInstanceLite {
  id: number;
  program_code: string;
  status?: string;
  start_date?: string | null;
  due_date?: string | null;
  origin?: string | null;
}

interface ApiResponse<T> { success: boolean; data?: T; message?: string; error?: string; }

class RiskScoresService {
  async listForRisk(riskId: number): Promise<RiskScore[]> {
    const res: AxiosResponse<ApiResponse<RiskScore[]>> = await apiClient.get(
      `/api/v1/rsk/risks/${riskId}/scores`,
    );
    return res.data?.data ?? [];
  }

  async latestForEntity(): Promise<RiskScore[]> {
    const res: AxiosResponse<ApiResponse<RiskScore[]>> = await apiClient.get(
      `/api/v1/rsk/risks/scores/latest`,
    );
    return res.data?.data ?? [];
  }

  async create(riskId: number, input: RiskScoreInput): Promise<RiskScore> {
    const res: AxiosResponse<ApiResponse<RiskScore>> = await apiClient.post(
      `/api/v1/rsk/risks/${riskId}/scores`,
      input,
    );
    if (!res.data?.data) throw new Error(res.data?.error || 'Failed to record score');
    return res.data.data;
  }

  async update(scoreId: number, input: Partial<RiskScoreInput>): Promise<RiskScore> {
    const res: AxiosResponse<ApiResponse<RiskScore>> = await apiClient.put(
      `/api/v1/rsk/scores/${scoreId}`,
      input,
    );
    if (!res.data?.data) throw new Error(res.data?.error || 'Failed to update score');
    return res.data.data;
  }

  async delete(scoreId: number): Promise<void> {
    await apiClient.delete(`/api/v1/rsk/scores/${scoreId}`);
  }

  async listActivePrograms(): Promise<AssessmentInstanceLite[]> {
    const res: AxiosResponse<ApiResponse<AssessmentInstanceLite[]>> = await apiClient.get(
      `/api/v1/rsk/assessment-instances/active`,
    );
    return res.data?.data ?? [];
  }
}

const riskScoresService = new RiskScoresService();
export default riskScoresService;
